import { useContext, useState } from "react";
import { toast } from "sonner";
import { PowerOff } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import UserContext from "@/contexts/UserContext";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";

const TOAST_ID = "conversation-disable-ai";

/**
 * "Desactivar IA": silences the agent on THIS conversation.
 *
 * One-way from the portal by design — `conversations.disableAi` only ever writes
 * `aiActive: false` and no procedure re-arms it, so the confirmation dialog stays:
 * a merchant who flips this cannot undo it here. Handing the lead back to the AI
 * means reassigning it.
 *
 * Only rendered by PerfilPanel when an AI agent actually owns the conversation and
 * is still active — see the `Manejado por` section.
 *
 * Wears the system's `outline` variant, the same costume AsesorControl's "Pasar a un
 * asesor" wears four hundred pixels to the left — because it is the same family of
 * action. That control ALSO silences the agent (leads.assignEmployee), it just names a
 * human as well; this one is the path to "AI off, nobody assigned". Dressing one of the
 * two in Stop Red made a handoff read as a deletion, and put red on screen twice with
 * two different meanings, since the confirmation below is where the system actually
 * spends red.
 */
const AgenteIaControl = ({
  merchantId,
  leadId,
  conversationId,
  surface,
}: {
  merchantId: string;
  leadId: string;
  conversationId: string;
  surface: "inbox" | "tablero";
}) => {
  const { email: callerEmail } = useContext(UserContext);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const utils = trpc.useUtils();

  const disableAi = trpc.conversations.disableAi.useMutation({
    onMutate: () => {
      toast.loading("Desactivando IA…", { id: TOAST_ID });
    },
    onSuccess: async () => {
      toast.success("IA desactivada", {
        id: TOAST_ID,
        description: "El agente de IA ya no responderá automáticamente a esta conversación.",
      });
      setIsConfirmOpen(false);
      posthog.capture("conversation ai disabled", {
        merchant_id: merchantId,
        lead_id: leadId,
        surface,
      });
      // The panel state, and the agentTurnedOff activity the mutation publishes,
      // which the timeline renders as "IA desactivada".
      await Promise.all([
        utils.conversations.get.invalidate({ merchantId, leadId }),
        utils.conversations.listConversationTimeline.invalidate({ merchantId, leadId }),
      ]);
    },
    onError: (error) => {
      toast.error("No se pudo desactivar la IA", { id: TOAST_ID, description: error.message });
    },
  });

  const handleDisableAi = () => {
    if (!callerEmail || disableAi.isLoading) return;
    disableAi.mutate({ merchantId, conversationId, leadId, userEmail: callerEmail });
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsConfirmOpen(true)}
        disabled={!callerEmail}
        // 44px touch target on mobile, where the perfil opens as a full sheet.
        className="mt-3 min-h-11 md:min-h-0"
      >
        {/* PowerOff, not Power: the ⏻ glyph is boot/shutdown, this turns one thing off. */}
        <PowerOff className="mr-1.5 size-4" />
        {/* The block above is titled "Agente IA" — it names the subject, so the button
            only has to name the verb. */}
        Desactivar
      </Button>

      <AlertDialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desactivar IA en esta conversación</AlertDialogTitle>
          </AlertDialogHeader>

          {/* The title already asks the question; the description answers "and then
              what". The four bullets this replaces were typed as literal "•" characters
              in separate <p> tags, so wrapped lines did not hang, and they sat in a
              warning Alert doing intimidation work the red confirm button below already
              does. The audit-trail line went with them: the mutation publishes an
              agentTurnedOff activity that the timeline renders as "IA desactivada", so
              the record exists where a reader would look for it rather than as a warning
              aimed at the person about to act. */}
          <AlertDialogDescription>
            El agente dejará de responder automáticamente. Los mensajes del cliente
            seguirán llegando y tendrás que contestarlos tú.
          </AlertDialogDescription>
          {/* The one thing that cannot be undone from here, in ink rather than in a
              coloured box — conversations.disableAi only ever writes aiActive: false and
              no procedure re-arms it. */}
          <p className="text-sm font-medium text-neutral-900">
            Para volver a activarla tendrás que reasignar el lead.
          </p>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDisableAi}
              disabled={disableAi.isLoading}
              className={buttonVariants({ variant: "destructive" })}
            >
              Desactivar IA
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AgenteIaControl;
